import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ArrowLeft, CheckCircle } from 'lucide-react';
import { authAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

export default function ResetPasswordPage() {
    const [params] = useSearchParams();
    const token = params.get('token') || '';
    const navigate = useNavigate();
    const [pw, setPw] = useState('');
    const [pw2, setPw2] = useState('');
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!token) { toast.error('Invalid or missing reset link'); return; }
        if (pw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
        if (pw !== pw2) { toast.error('Passwords do not match'); return; }
        setBusy(true);
        try {
            await authAPI.confirmPasswordReset(token, pw);
            setDone(true);
            toast.success('Password updated — you can sign in now');
            setTimeout(() => navigate('/login'), 1800);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Reset link is invalid or has expired');
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-muted/30">
            <Card className="w-full max-w-md">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2"><Lock className="h-5 w-5 text-primary" /> Set a new password</CardTitle>
                    <CardDescription>Choose a new password for your Accountrix account.</CardDescription>
                </CardHeader>
                <CardContent>
                    {done ? (
                        <div className="text-center space-y-3 py-4">
                            <CheckCircle className="h-10 w-10 text-emerald-600 mx-auto" />
                            <p className="text-sm text-muted-foreground">Password updated. Redirecting to sign in…</p>
                        </div>
                    ) : !token ? (
                        <p className="text-sm text-red-600">This reset link is invalid or incomplete. Request a new one from “Forgot password”.</p>
                    ) : (
                        <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
                            <div className="space-y-1">
                                <Label htmlFor="pw">New password</Label>
                                <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" required />
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="pw2">Confirm new password</Label>
                                <Input id="pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" required />
                            </div>
                            <Button type="submit" className="w-full" disabled={busy} data-testid="reset-submit">
                                {busy ? 'Updating…' : 'Update password'}
                            </Button>
                        </form>
                    )}
                    <div className="mt-4 text-center">
                        <Link to="/login" className="text-sm text-muted-foreground inline-flex items-center gap-1"><ArrowLeft className="h-3 w-3" /> Back to sign in</Link>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
